import { IconProps } from "@/components/shared/Icon"
import { TestId } from "@/constants/globals"
import { WsFeedPayload } from "@/hooks/useRealtimeConnection"
import { Alert } from "@/types/alert"
import { Cell } from "@/types/cell"

export function getCellIconName(
   isCustom?: boolean,
   hasDoorSensor?: boolean,
   doorSensorLowBattery?: boolean
): IconProps["name"] {
   if (hasDoorSensor)
      return doorSensorLowBattery ? "batteryEmpty" : "cellWithDoorSensor"
   if (isCustom) return "cellCustom"
   return "cell"
}

export function getCellIconTestId(iconName: IconProps["name"]): TestId {
   switch (iconName) {
      case "cellCustom":
         return TestId.CELL_CUSTOM_ICON
      case "cellWithDoorSensor":
         return TestId.CELL_WITH_DOOR_SENSOR_ICON
      case "batteryEmpty":
         return TestId.BATTERY_EMPTY_ICON
      case "cell":
      default:
         return TestId.CELL_ICON
   }
}

export function getCellStatusIconTestId(
   iconName: IconProps["name"] | null
): TestId | undefined {
   switch (iconName) {
      case "maintenance":
         return TestId.MAINTENANCE_ICON
      case "disconnected":
         return TestId.DISCONNECTED_ICON
      case "humanPresence":
         return TestId.HUMAN_PRESENCE_ICON
      case "movement":
         return TestId.MOVEMENT_ICON
      default:
         return undefined
   }
}

export function getHighestPrioAlert(alerts: Alert[]): Alert | undefined {
   if (!alerts.length) return undefined
   return alerts.reduce((lowest, current) =>
      current.priority < lowest.priority ? current : lowest
   )
}

export function mapWsFeedToCell(
   cell: Cell,
   payload: WsFeedPayload
): Partial<Cell> {
   const raw = payload.data ?? {}
   const control = payload.control ?? {}

   const hasVitals = raw.hr !== undefined && raw.br !== undefined
   const hasDoorSensorData = raw.battery !== undefined

   return {
      ...(control.vacant !== undefined && { vacant: control.vacant }),
      ...(control.maintenance !== undefined && {
         maintenance: control.maintenance,
      }),
      ...(control.hasCustomConfigurations !== undefined && {
         hasCustomConfigurations: control.hasCustomConfigurations,
      }),
      ...(hasDoorSensorData && {
         doorSensorData: { battery: Number(raw.battery) },
      }),
      data: {
         ...cell.data,
         status: hasDoorSensorData
            ? cell.data.status
            : {
                 ...cell.data.status,
                 humanPresence: raw.presence === 1,
                 sensorConnected: true,
              },
         vitals: hasVitals
            ? {
                 heartRate: Number(raw.hr),
                 heartRateStabilityScore: Number(raw.hrConfidence ?? 0),
                 respiratoryRate: Number(raw.br),
                 respiratoryStabilityScore: Number(raw.brConfidence ?? 0),
              }
            : cell.data.vitals,
      },
   }
}
